import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  RadioGroup,
  FormControlLabel,
  Radio,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  InputAdornment,
  Fade,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  Visibility as VisibilityIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterListIcon,
  TableSortLabel,
  MoreVert as MoreVertIcon,
  GetApp as GetAppIcon,
  Payment as PaymentIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import accountsApiService from '../../../services/accountsApiService';
import { redirect, useNavigate } from 'react-router-dom';

const PaymentDetails = ({ showSnackbar, setLoading, loading }) => {
  const navigate = useNavigate();

  // Helper function to format date to SQL format
  const formatDateToSQL = (date) => {
    if (!date) return '';
    
    // Handle dayjs objects
    if (date && typeof date.format === 'function') {
      return date.format('YYYY-MM-DD');
    }
    
    // Handle JavaScript Date objects
    if (date instanceof Date) {
      return dayjs(date).format('YYYY-MM-DD');
    }
    
    // Handle string dates
    if (typeof date === 'string') {
      return dayjs(date).format('YYYY-MM-DD');
    }
    
    return '';
  };

  // State management
  const [filters, setFilters] = useState({
    transactionType: '',
    transactionStatus: '1', // Default to success transactions
    labType: '',
    userName: '',
    orderId: '',
    applicationType: '',
    dateFrom: null,
    dateTo: null,
  });

  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Dropdown options
  const [transactionTypes, setTransactionTypes] = useState([]);
  const [labTypes, setLabTypes] = useState([]);
  const [applicationTypes, setApplicationTypes] = useState([]);

  // Load dropdown options
  useEffect(() => {
    loadDropdownData();
  }, []);

  const loadDropdownData = async () => {
    try {
      setLoading(true);

      // Load real data from API
      const [paymentTypesResponse, assessmentTypesResponse] = await Promise.all([
        accountsApiService.getPaymentTypes(),
        accountsApiService.getAssessmentTypes()
      ]);

      if (paymentTypesResponse.success) {
        setTransactionTypes(paymentTypesResponse.data || []);
      }

      if (assessmentTypesResponse.success) {
        setApplicationTypes(assessmentTypesResponse.data || []);
      }

      // Set lab types from service
      setLabTypes(accountsApiService.getLabTypes());

      // Load initial data
      await loadPaymentData();
    } catch (error) {
      console.error('Error loading dropdown data:', error);
      showSnackbar(error.message || 'Error loading dropdown data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadPaymentData = async (filterParams = {}) => {
    try {
      setLoading(true);

      // Use current page and rowsPerPage unless overridden in filterParams
      const currentPage = filterParams.hasOwnProperty('page') ? filterParams.page : page;
      const currentPageSize = filterParams.hasOwnProperty('pageSize') ? filterParams.pageSize : rowsPerPage;

      // Prepare API parameters
      const apiParams = {
        transactionStatus: filterParams.transactionStatus || filters.transactionStatus,
        labType: filterParams.labType || filters.labType,
        transactionType: filterParams.transactionType || filters.transactionType,
        userName: filterParams.userName || filters.userName,
        orderId: filterParams.orderId || filters.orderId,
        applicationType: filterParams.applicationType || filters.applicationType,
        dateFrom: formatDateToSQL(filterParams.dateFrom || filters.dateFrom),
        dateTo: formatDateToSQL(filterParams.dateTo || filters.dateTo),
        page: currentPage + 1, // API expects 1-based page numbers
        pageSize: currentPageSize
      };

      const response = await accountsApiService.getPaymentDetails(apiParams);

      console.log('API Response:', response); // Debug log
      console.log('API Params:', apiParams); // Debug log

      if (response.success) {
        setData(response.data || []);
        setFilteredData(response.data || []);
        setTotalRecords(response.pagination?.totalRecords || 0);

        console.log('Data loaded:', response.data?.length || 0, 'records'); // Debug log

        // Only reset page to 0 if we're doing a new search (not pagination)
        if (!filterParams.hasOwnProperty('page')) {
          setPage(0);
        }
      } else {
        console.error('API Error:', response.message); // Debug log
        showSnackbar(response.message || 'Failed to load payment data', 'error');
        setData([]);
        setFilteredData([]);
        setTotalRecords(0);
      }
    } catch (error) {
      console.error('Error loading payment data:', error);
      showSnackbar(error.message || 'Error loading payment data', 'error');
      setData([]);
      setFilteredData([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  // Get current page data
  const getCurrentPageData = () => {
    return filteredData; // API handles pagination
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSearch = () => {
    loadPaymentData(filters);
  };

  const handleReset = () => {
    setFilters({
      transactionType: '',
      transactionStatus: '',
      labType: '',
      userName: '',
      orderId: '',
      applicationType: '',
      dateFrom: null,
      dateTo: null,
    });
    loadPaymentData();
  };

  const handleExcelExport = async () => {
    try {
      setLoading(true);

      // Prepare export parameters
      const exportParams = {
        transactionStatus: filters.transactionStatus,
        labType: filters.labType,
        transactionType: filters.transactionType,
        userName: filters.userName,
        orderId: filters.orderId,
        applicationType: filters.applicationType,
        dateFrom: formatDateToSQL(filters.dateFrom),
        dateTo: formatDateToSQL(filters.dateTo),
      };

      const response = await accountsApiService.exportPaymentDetails(exportParams);

      if (response.success) {
        showSnackbar('Excel export completed successfully', 'success');
      } else {
        showSnackbar(response.message || 'Export failed', 'error');
      }
    } catch (error) {
      console.error('Error exporting data:', error);
      showSnackbar(error.message || 'Error exporting data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    // Reload data with new page
    loadPaymentData({ ...filters, page: newPage });
  };

  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Reload data with new page size
    loadPaymentData({ ...filters, page: 0, pageSize: newRowsPerPage });
  };

  const getStatusChip = (status) => {
    const normalizedStatus = status?.toLowerCase();

    if (normalizedStatus === 'success') {
      return (
        <Chip
          label="Success"
          size="small"
          color="success"
          variant="filled"
          sx={{
            fontWeight: 600,
            minWidth: 80,
            '&.MuiChip-colorSuccess': {
              bgcolor: 'success.main',
              color: 'white'
            }
          }}
        />
      );
    } else if (normalizedStatus === 'failed' || normalizedStatus === 'fail') {
      return (
        <Chip
          label="Failed"
          size="small"
          color="error"
          variant="filled"
          sx={{
            fontWeight: 600,
            minWidth: 80,
            '&.MuiChip-colorError': {
              bgcolor: 'error.main',
              color: 'white'
            }
          }}
        />
      );
    } else if (normalizedStatus === 'pending') {
      return (
        <Chip
          label="Pending"
          size="small"
          color="warning"
          variant="filled"
          sx={{
            fontWeight: 600,
            minWidth: 80,
            '&.MuiChip-colorWarning': {
              bgcolor: 'warning.main',
              color: 'white'
            }
          }}
        />
      );
    } else {
      return (
        <Chip
          label={status || 'Unknown'}
          size="small"
          color="default"
          variant="outlined"
          sx={{
            fontWeight: 500,
            minWidth: 80
          }}
        />
      );
    }
  };

  const handleViewInvoice = (row) => {

    const rawPath = row?.paths || row?.Paths || '';
    const apipath = rawPath.replace(/^~\/?/, '');
    const orgid = localStorage.getItem('orgId'); // Assuming orgId is stored in localStorage after login
    window.open(
      `https://nablwpnew.qci.org.in/${apipath}&orgid=${orgid}`,
      "_blank",
      "noopener,noreferrer"
    );


  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ p: 2 }}>
        {/* Filter Section */}
        <Card elevation={2} sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
              <FilterListIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                Payment Details Filters
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                <Button
                  variant="outlined"
                  onClick={handleReset}
                  sx={{ mr: 1 }}
                  startIcon={<RefreshIcon />}
                  size="small"
                >
                  Reset
                </Button>
                {/* <Button
                  variant="contained"
                  onClick={handleExcelExport}
                  startIcon={<FileDownloadIcon />}
                  size="small"
                >
                  Excel Export
                </Button> */}
              </Box>
            </Box>

            {/* Transaction Status */}
            <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Transaction Status:
              </Typography>
              <RadioGroup
                row
                value={filters.transactionStatus}
                onChange={(e) => handleFilterChange('transactionStatus', e.target.value)}
                sx={{ gap: 3 }}
              >
                <FormControlLabel
                  value="1"
                  control={<Radio color="success" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label="Success" color="success" size="small" sx={{ mr: 1 }} />
                      Transactions
                    </Box>
                  }
                />
                <FormControlLabel
                  value="2"
                  control={<Radio color="error" />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Chip label="Failed" color="error" size="small" sx={{ mr: 1 }} />
                      Transactions
                    </Box>
                  }
                />
              </RadioGroup>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 500 }}>Transaction Type</InputLabel>
                  <Select
                    value={filters.transactionType}
                    label="Transaction Type"
                    onChange={(e) => handleFilterChange('transactionType', e.target.value)}
                    sx={{ '& .MuiSelect-select': { fontWeight: 500 } }}
                  >
                    <MenuItem value="">
                      <em>All Types</em>
                    </MenuItem>
                    {transactionTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Order ID"
                  value={filters.orderId}
                  onChange={(e) => handleFilterChange('orderId', e.target.value)}
                  placeholder="Enter Order ID"
                  InputLabelProps={{ sx: { fontWeight: 500 } }}
                  InputProps={{
                    sx: { fontWeight: 500 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 500 }}>Lab Type</InputLabel>
                  <Select
                    value={filters.labType}
                    label="Lab Type"
                    onChange={(e) => handleFilterChange('labType', e.target.value)}
                    sx={{ '& .MuiSelect-select': { fontWeight: 500 } }}
                  >
                    <MenuItem value="">
                      <em>All Lab Types</em>
                    </MenuItem>
                    {labTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="User Name/Lab ID"
                  value={filters.userName}
                  onChange={(e) => handleFilterChange('userName', e.target.value)}
                  placeholder="Enter Username or Lab ID"
                  InputLabelProps={{ sx: { fontWeight: 500 } }}
                  InputProps={{
                    sx: { fontWeight: 500 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon sx={{ color: 'text.secondary', fontSize: '1rem' }} />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel sx={{ fontWeight: 500 }}>Application Type</InputLabel>
                  <Select
                    value={filters.applicationType}
                    label="Application Type"
                    onChange={(e) => handleFilterChange('applicationType', e.target.value)}
                    sx={{ '& .MuiSelect-select': { fontWeight: 500 } }}
                  >
                    <MenuItem value="">
                      <em>All Application Types</em>
                    </MenuItem>
                    {applicationTypes.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            {/* Date Range Filter */}
            <Paper elevation={1} sx={{ p: 3, mt: 3, bgcolor: 'primary.50', borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: 'primary.main', fontWeight: 600 }}>
                Date Range Filter:
              </Typography>
              <Grid container spacing={2} alignItems="end">
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Date From"
                    value={filters.dateFrom}
                    onChange={(newValue) => handleFilterChange('dateFrom', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputLabelProps={{ sx: { fontWeight: 500 } }}
                      />
                    )}
                    maxDate={dayjs()}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <DatePicker
                    label="Date To"
                    value={filters.dateTo}
                    onChange={(newValue) => handleFilterChange('dateTo', newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        size="small"
                        InputLabelProps={{ sx: { fontWeight: 500 } }}
                      />
                    )}
                    maxDate={dayjs()}
                    minDate={filters.dateFrom}
                  />
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <Button
                    variant="contained"
                    onClick={handleSearch}
                    startIcon={<SearchIcon />}
                    fullWidth
                    size="large"
                    sx={{
                      py: 1.5,
                      fontWeight: 600,
                      boxShadow: 2,
                      '&:hover': {
                        boxShadow: 4,
                      }
                    }}
                  >
                    Search Records
                  </Button>
                </Grid>
              </Grid>
            </Paper>
          </CardContent>
        </Card>

        {/* Data Table */}
        <Card elevation={2}>
          <CardContent sx={{ p: 0 }}>
            {/* Table Header */}
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <PaymentIcon sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                    Payment Records
                  </Typography>
                  <Chip
                    label={`${totalRecords} Total`}
                    size="small"
                    sx={{ ml: 2 }}
                    color="primary"
                    variant="outlined"
                  />
                </Box>
                {/* <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<GetAppIcon />}
                    onClick={handleExcelExport}
                    disabled={loading || filteredData.length === 0}
                  >
                    Export
                  </Button>
                </Box> */}
              </Box>
            </Box>

            {loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', p: 8 }}>
                <CircularProgress size={40} />
                <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
                  Loading payment data...
                </Typography>
              </Box>
            ) : filteredData.length === 0 ? (
              <Box sx={{ p: 8, textAlign: 'center' }}>
                <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
                <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
                  No Payment Data Found
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  Try adjusting your filters to see payment records.
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer sx={{ maxHeight: 600 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 80 }}>
                          Actions
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 60 }}>
                          S. No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Invoice No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Lab ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 200 }}>
                          Laboratory Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Order ID
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Fees Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Payment Mode
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 130 }}>
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 150 }}>
                          Transaction Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          GSTIN
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          TDS
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          TAN
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          User Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Lab Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 140 }}>
                          Assessment Type
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 140 }}>
                          Contact Person
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 180 }}>
                          Email
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Mobile
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 200 }}>
                          Address
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          State
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          City
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 80 }}>
                          Pincode
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Discipline
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Group
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Collection Count
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Lab Size
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 100 }}>
                          Lab Count
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 140 }}>
                          Certificate No
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Issue Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 120 }}>
                          Valid Till
                        </TableCell>
                        <TableCell sx={{ fontWeight: 600, bgcolor: 'primary.main', color: 'white', minWidth: 140 }}>
                          Dealing Officer
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {getCurrentPageData().map((row, index) => (
                        <TableRow
                          key={row.ApplicationNo || row.order_id || index}
                          hover
                          sx={{
                            '&:nth-of-type(odd)': { bgcolor: 'grey.25' },
                            '&:hover': { bgcolor: 'primary.50' }
                          }}
                        >
                          <TableCell>
                            {row.viewInvoice === "1" && row.order_status?.toLowerCase() === 'success' && (
                              <Tooltip title="View Invoice" placement="top">
                                <IconButton
                                  size="small"
                                  onClick={() => handleViewInvoice(row)}
                                  sx={{
                                    color: 'primary.main',
                                    '&:hover': { bgcolor: 'primary.100' }
                                  }}
                                >
                                  <ReceiptIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.InvoiceNo || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem', color: 'primary.main', fontWeight: 500 }}>
                            {row.ApplicationNo || '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.LaboratoryName || '-'}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.order_id || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.Description || 'Unknown'}
                              size="small"
                              variant="outlined"
                              color="info"
                            />
                          </TableCell>
                          <TableCell>
                            {getStatusChip(row.order_status || 'Unknown')}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.payment_mode || 'Online'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontWeight: 600, color: 'success.main' }}>
                            ₹{row.TotalAmount
                              ? parseFloat(row.TotalAmount).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              })
                              : row.amount ? 
                               parseFloat(row.amount).toLocaleString('en-IN', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2
                              }) : '0.00'
                            }
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.TransactionDateTime
                              ? new Date(row.TransactionDateTime).toLocaleDateString('en-IN', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })
                              : '-'
                            }
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.gst || '-'}
                          </TableCell>
                          <TableCell>
                            {row.DeductTDS === "Yes" ? (
                              <Chip label="Yes" size="small" color="warning" />
                            ) : (
                              <Chip label="No" size="small" color="default" />
                            )}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.Tanno || '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {row.loginname || '-'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={row.LabType || 'Unknown'}
                              size="small"
                              color="secondary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{row.AppAssessmentType || '-'}</TableCell>
                          <TableCell>{row.ContactPerson || '-'}</TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.ContactEmail || '-'}
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.ContactMobile || '-'}
                          </TableCell>
                          <TableCell>
                            <Box sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {row.Address || '-'}
                            </Box>
                          </TableCell>
                          <TableCell>{row.State || '-'}</TableCell>
                          <TableCell>{row.City || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>{row.Pin || '-'}</TableCell>
                          <TableCell>{row.Disciplines || '-'}</TableCell>
                          <TableCell>{row.Groups || '-'}</TableCell>
                          <TableCell>{row.Collection || '-'}</TableCell>
                          <TableCell>{row.SizeOflaboratory || '-'}</TableCell>
                          <TableCell>{row.CountOflaboratory || '-'}</TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                            {row.CertificateNo || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.IssueDate || '-'}
                          </TableCell>
                          <TableCell sx={{ fontSize: '0.875rem' }}>
                            {row.ValidTill || '-'}
                          </TableCell>
                          <TableCell>{row.DoName || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>

                {/* Enhanced Pagination */}
                <Box sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                  <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    component="div"
                    count={totalRecords}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Rows per page:"
                    labelDisplayedRows={({ from, to, count }) =>
                      `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
                    }
                    sx={{
                      '& .MuiTablePagination-toolbar': {
                        paddingLeft: 2,
                        paddingRight: 2,
                      },
                      '& .MuiTablePagination-selectLabel': {
                        fontWeight: 500,
                      },
                      '& .MuiTablePagination-displayedRows': {
                        fontWeight: 500,
                      }
                    }}
                  />
                </Box>
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </LocalizationProvider>
  );
};

export default PaymentDetails;